/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

export enum Permission {
    /**
     * Create Partner SPOs
     */
    PARTNER_SPO_CREATE = 'partner:spo:create',
    /**
     * Edit Partner SPOs
     */
    PARTNER_SPO_EDIT = 'partner:spo:edit',
    /**
     * Delete Partner SPOs
     */
    PARTNER_SPO_DELETE = 'partner:spo:delete',
    /**
     * Send Partner SPOs
     */
    PARTNER_SPO_SEND = 'partner:spo:send',
    /**
     * Create and send Partner SPOs
     */
    PARTNER_SPO_CREATE_SEND = 'partner:spo:create-send',
    /**
     * Cancel Partner SPOs
     */
    PARTNER_SPO_CANCEL = 'partner:spo:cancel',
    /**
     * View Partner SPOs
     */
    PARTNER_SPO_VIEW = 'partner:spo:view',
    /**
     * Grant Partner business roles to users and manage user Partners as a subset of the authenticated user Partners
     */
    PARTNER_ROLE_GRANT = 'partner:role:grant',
    /**
     * Revoke Partner business roles to users and manage user Partners as a subset of the authenticated user Partners
     */
    PARTNER_ROLE_REVOKE = 'partner:role:revoke',
    /**
     * View Partner business roles and the users Partners
     */
    PARTNER_ROLE_VIEW = 'partner:role:view',
    /**
     * Export Partner SPOs
     */
    PARTNER_SPO_EXPORT = 'partner:spo:export',
    /**
     * View all SPOs
     */
    ADMIN_SPO_VIEW = 'admin:spo:view',
    /**
     * Cancel all SPOs
     */
    ADMIN_SPO_CANCEL = 'admin:spo:cancel',
    /**
     * Grant roles to users and manage user Partners
     */
    ADMIN_ROLE_GRANT = 'admin:role:grant',
    /**
     * Revoke roles to users and manage user Partners
     */
    ADMIN_ROLE_REVOKE = 'admin:role:revoke',
    /**
     * View all roles
     */
    ADMIN_ROLE_VIEW = 'admin:role:view',
    /**
     * Export all SPOs
     */
    ADMIN_SPO_EXPORT = 'admin:spo:export',
    /**
     * Export all Claims
     */
    ADMIN_CLAIM_EXPORT = 'admin:claim:export',
    /**
     * Create Partner Claims
     */
    PARTNER_CLAIM_CREATE = 'partner:claim:create',
    /**
     * Edit Partner Claims
     */
    PARTNER_CLAIM_EDIT = 'partner:claim:edit',
    /**
     * Delete Partner Claims
     */
    PARTNER_CLAIM_DELETE = 'partner:claim:delete',
    /**
     * Send Partner Claims
     */
    PARTNER_CLAIM_SEND = 'partner:claim:send',
    /**
     * Create and send Partner Claims
     */
    PARTNER_CLAIM_CREATE_SEND = 'partner:claim:create-send',
    /**
     * View Partner Claims
     */
    PARTNER_CLAIM_VIEW = 'partner:claim:view',
    /**
     * View all Claims
     */
    ADMIN_CLAIM_VIEW = 'admin:claim:view',
    /**
     * Export Partner Claims
     */
    PARTNER_CLAIM_EXPORT = 'partner:claim:export',
    /**
     * Create Partner directories
     */
    PARTNER_DIRECTORY_CREATE = 'partner:directory:create',
    /**
     * Upload Partner files
     */
    PARTNER_FILE_UPLOAD = 'partner:file:upload',
    /**
     * Delete Partner files and directories
     */
    PARTNER_DIRECTORY_DELETE = 'partner:directory:delete',
    /**
     * Grant the right to view Claims attachments
     */
    ADMIN_CLAIM_VIEW_BASE = 'admin:claim:view_base',
    /**
     * View business settings.
     */
    ADMIN_BUSINESS_SETTINGS_VIEW = 'admin:business-settings:view',
    /**
     * Edit business settings.
     */
    ADMIN_BUSINESS_SETTINGS_EDIT = 'admin:business-settings:edit',
    /**
     * Send business settings.
     */
    ADMIN_BUSINESS_SETTINGS_SEND = 'admin:business-settings:send',
    /**
     * Admin part number changes view.
     */
    ADMIN_PNC_VIEW = 'admin:pnc:view',
    /**
     * Admin part number changes export excel
     */
    ADMIN_PNC_EXPORT = 'admin:pnc:export',
    /**
     * Partner part number changes create.
     */
    PARTNER_PNC_CREATE = 'partner:pnc:create',
    /**
     * Partner part number changes view.
     */
    PARTNER_PNC_VIEW = 'partner:pnc:view',
    /**
     * Partner part number changes send
     */
    PARTNER_PNC_SEND = 'partner:pnc:send',
    /**
     * Partner part number changes export
     */
    PARTNER_PNC_EXPORT = 'partner:pnc:export',
    /**
     * Price planner part number change view
     */
    PRICE_PLANNER_PNC_VIEW = 'price-planner:pnc:view',
    /**
     * Price planner part number change view
     */
    PRICE_PLANNER_PNC_EXPORT = 'price-planner:pnc:export',
    /**
     * Planner part number change create
     */
    PLANNER_PNC_CREATE = 'planner:pnc:create',
    /**
     * Planner part number change send
     */
    PLANNER_PNC_SEND = 'planner:pnc:send',
    /**
     * Planner part number change create and solve
     */
    PLANNER_PNC_CREATE_SOLVE = 'planner:pnc:create-solve',
    /**
     * Planner part number change view
     */
    PLANNER_PNC_VIEW = 'planner:pnc:view',
    /**
     * Planner part number change export excel
     */
    PLANNER_PNC_EXPORT = 'planner:pnc:export',
    /**
     * Technician part number change view
     */
    TECHNICIAN_PNC_VIEW = 'technician:pnc:view',
    /**
     * Technician part number change reject
     */
    TECHNICIAN_PNC_REJECT = 'technician:pnc:reject',
    /**
     * Technician part number change solve
     */
    TECHNICIAN_PNC_SOLVE = 'technician:pnc:solve',
    /**
     * Technician part number change export excel
     */
    TECHNICIAN_PNC_EXPORT = 'technician:pnc:export',
    /**
     * EE Specialist part number change view
     */
    EE_SPECIALIST_PNC_VIEW = 'ee-specialist:pnc:view',
    /**
     * EE Specialist part number change accept
     */
    EE_SPECIALIST_PNC_ACCEPT = 'ee-specialist:pnc:accept',
    /**
     * EE Specialist part number change reject
     */
    EE_SPECIALIST_PNC_REJECT = 'ee-specialist:pnc:reject',
    /**
     * Admin settlement view
     */
    ADMIN_SETTLEMENT_VIEW = 'admin:settlement:view',
    /**
     * Admin settlement export
     */
    ADMIN_SETTLEMENT_EXPORT = 'admin:settlement:export',
    /**
     * Supplier settlement view
     */
    SUPPLIER_SETTLEMENT_VIEW = 'supplier:settlement:view',
    /**
     * Supplier settlement export
     */
    SUPPLIER_SETTLEMENT_EXPORT = 'supplier:settlement:export',
    /**
     * Supplier settlements accept
     */
    SUPPLIER_SETTLEMENT_ACCEPT = 'supplier:settlement:accept',
    /**
     * Supplier settlements reject
     */
    SUPPLIER_SETTLEMENT_REJECT = 'supplier:settlement:reject',
    /**
     * 4eyes settlement view
     */
    FOUR_EYES_SETTLEMENT_VIEW = '4eyes:settlement:view',
    /**
     * 4eyes settlement accept
     */
    FOUR_EYES_SETTLEMENT_ACCEPT = '4eyes:settlement:accept',
    /**
     * 4eyes settlement reject
     */
    FOUR_EYES_SETTLEMENT_REJECT = '4eyes:settlement:reject',
    /**
     * 4eyes settlement export
     */
    FOUR_EYES_SETTLEMENT_EXPORT = '4eyes:settlement:export',
    /**
     * Bundle view
     */
    ADMIN_BUNDLE_VIEW = 'admin:bundle:view',
    /**
     * Bundle period view
     */
    ADMIN_BUNDLES_PERIOD_VIEW = 'admin:bundles-period:view',
    /**
     * Bundle export
     */
    ADMIN_BUNDLE_EXPORT = 'admin:bundle:export',
    /**
     * Bundles period manage
     */
    ADMIN_BUNDLES_PERIOD_MANAGE = 'admin:bundles-period:manage',
    /**
     * Bundles close
     */
    ADMIN_BUNDLE_CLOSE = 'admin:bundle:close',
    /**
     * Bundles settings view
     */
    ADMIN_BUNDLES_SETTINGS_VIEW = 'admin:bundles-settings:view',
    /**
     * Bundles settings manage
     */
    ADMIN_BUNDLES_SETTINGS_MANAGE = 'admin:bundles-settings:manage',
    /**
     * Customer bundle view
     */
    CUSTOMER_BUNDLE_VIEW = 'customer:bundle:view',
    /**
     * Customer bundle period view
     */
    CUSTOMER_BUNDLES_PERIOD_VIEW = 'customer:bundles-period:view',
    /**
     * Customer bundle export
     */
    CUSTOMER_BUNDLE_EXPORT = 'customer:bundle:export',

    /**
     * Admin bundle sent
     */
    ADMIN_BUNDLE_SENT = 'admin:bundle:sent',

    /**
     * Admin auto close bundles view
     */
    ADMIN_BUNDLES_AUTOCLOSEBUNDLES_VIEW = 'admin:bundles-autoCloseBundles:view',

    /**
     * Admin auto close bundles manage
     */
    ADMIN_BUNDLES_AUTOCLOSEBUNDLES_MANAGE = 'admin:bundles-autoCloseBundles:manage',
    /**
     * Admin alerts config view
     */
    ADMIN_ALERTS_CONFIG_VIEW = 'admin:alert-config:view',
    /**
     * Admin alerts config create
     */
    ADMIN_ALERTS_CONFIG_CREATE = 'admin:alert-config:create',
    /**
     * Admin alerts config to edit
     */
    ADMIN_ALERTS_CONFIG_EDIT = 'admin:alert-config:edit',
    /**
     * Admin alerts config to delete
     */
    ADMIN_ALERTS_CONFIG_DELETE = 'admin:alert-config:delete',
    /**
     * Unknown permission
     */
    UNKNOWN = 'UNKNOWN',
}
